import React, { useState, useMemo } from 'react';

// Mapping of airport codes to IANA timezone names
const airportTimezones = {
  'HND': 'Asia/Tokyo', // Tokyo Haneda, Japan (UTC+09:00)
  'NRT': 'Asia/Tokyo', // Tokyo Narita, Japan (UTC+09:00)
  'KIX': 'Asia/Tokyo', // Osaka, Japan (UTC+09:00)
  'NGO': 'Asia/Tokyo', // Nagoya, Japan (UTC+09:00)
  'HNL': 'Pacific/Honolulu', // Honolulu, Hawaii (UTC-10:00)
  'JFK': 'America/New_York', // New York JFK (UTC-05:00/-04:00 with DST)
  'BOS': 'America/New_York', // Boston (UTC-05:00/-04:00 with DST)
  'ORD': 'America/Chicago', // Chicago (UTC-06:00/-05:00 with DST)
  'DFW': 'America/Dallas', // Dallas (UTC-06:00/-05:00 with DST)
  'LAX': 'America/Los_Angeles', // Los Angeles (UTC-08:00/-07:00 with DST)
  'SAN': 'America/Los_Angeles', // San Diego (UTC-08:00/-07:00 with DST)
  'SFO': 'America/Los_Angeles', // San Francisco (UTC-08:00/-07:00 with DST)
  'SEA': 'America/Los_Angeles', // Seattle (UTC-08:00/-07:00 with DST)
  'YVR': 'America/Los_Angeles', // Vancouver (UTC-08:00/-07:00 with DST)
};

const FlightTable = ({ flights, loading }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'points',
    direction: 'ascending'
  });
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Formatting functions
  const formatDate = (timeStr, origin) => {
    if (!timeStr || !origin) return '';
    const timezone = airportTimezones[origin] || 'UTC'; // Fallback to UTC if timezone not found
    const date = new Date(timeStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timezone
    });
  };
  
  const formatTime = (timeStr, airport) => {
    if (!timeStr || !airport) return '';
    const timezone = airportTimezones[airport] || 'UTC'; // Fallback to UTC if timezone not found
    const date = new Date(timeStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    });
  };
  
  // Handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    
    setSortConfig({ key, direction });
  };
  
  // Sort the flights
  const sortedFlights = useMemo(() => {
    if (!flights || flights.length === 0) return [];
    
    const sortableFlights = [...flights];
    
    sortableFlights.sort((a, b) => {
      // Handle nulls
      if (a[sortConfig.key] === null) return 1;
      if (b[sortConfig.key] === null) return -1;
      
      // Special handling for dates (use depart_time instead of depart_date)
      if (sortConfig.key === 'depart_date') {
        const dateA = new Date(a.depart_time);
        const dateB = new Date(b.depart_time);
        
        if (sortConfig.direction === 'ascending') {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      }
      
      // Special handling for times
      if (sortConfig.key === 'depart_time') {
        const timeA = new Date(a.depart_time);
        const timeB = new Date(b.depart_time);
        
        if (sortConfig.direction === 'ascending') {
          return timeA - timeB;
        } else {
          return timeB - timeA;
        }
      }
      
      // Default comparison
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableFlights;
  }, [flights, sortConfig]);
  
  // Get sort indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    
    return (
      <span className="ml-1">
        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
      </span>
    );
  };
  
  // Handle row expansion
  const toggleRowExpansion = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(id);
    }
  };
  
  // Get class label color
  const getClassColor = (cabinClass) => {
    switch (cabinClass?.toLowerCase()) {
      case 'economy':
        return 'bg-blue-100 text-blue-800';
      case 'business':
        return 'bg-purple-100 text-purple-800';
      case 'first':
        return 'bg-indigo-100 text-indigo-800';
      case 'mint':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Safely parse connections
  const parseConnections = (connections) => {
    if (!connections) return [];
    
    // If connections is already an array of objects, return it
    if (Array.isArray(connections) && connections.every(item => typeof item === 'object' && item.airport && item.duration)) {
      return connections;
    }
    
    // If connections is an array of strings (e.g., ["KIX"]), transform it
    if (Array.isArray(connections) && connections.every(item => typeof item === 'string')) {
      return connections.map(airport => ({
        airport: airport,
        duration: 'Unknown' // Duration not provided by API
      }));
    }
    
    // If connections is a JSON string, try parsing it
    try {
      const parsed = JSON.parse(connections);
      if (Array.isArray(parsed)) {
        // If parsed array contains strings (e.g., ["KIX"])
        if (parsed.every(item => typeof item === 'string')) {
          return parsed.map(airport => ({
            airport: airport,
            duration: 'Unknown' // Duration not provided by API
          }));
        }
        // If parsed array contains objects, return as is
        return parsed;
      }
      return [];
    } catch (e) {
      console.error('Error parsing connections:', e);
      return [];
    }
  };

  // Safely parse operating_airlines
  const parseAirlines = (operating_airlines) => {
    if (!operating_airlines) return [];
    
    // If operating_airlines is already an array of objects, return it
    if (Array.isArray(operating_airlines) && operating_airlines.every(item => typeof item === 'object' && item.airport && item.duration)) {
      return operating_airlines;
    }
    
    // If operating_airlines is an array of strings (e.g., ["JL"]), transform it
    if (Array.isArray(operating_airlines) && operating_airlines.every(item => typeof item === 'string')) {
      return operating_airlines.map(airline => ({
        airline: airline,
      }));
    }
    
    // If operating_airlines is a JSON string, try parsing it
    try {
      const parsed = JSON.parse(operating_airlines);
      if (Array.isArray(parsed)) {
        // If parsed array contains strings (e.g., ["JL"])
        if (parsed.every(item => typeof item === 'string')) {
          return parsed.map(airline => ({
            airline: airline,
          }));
        }
        // If parsed array contains objects, return as is
        return parsed;
      }
      return [];
    } catch (e) {
      console.error('Error parsing operating_airlines:', e);
      return [];
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="text-gray-500">Loading flight data...</div>
      </div>
    );
  }
  
  if (!flights || flights.length === 0) {
    return (
      <div className="text-center p-8 text-gray-500">
        No flights found for the selected criteria.
      </div>
    );
  }
  
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button 
                className="font-medium text-left flex items-center"
                onClick={() => handleSort('depart_date')}
              >
                Date
                {getSortDirectionIndicator('depart_date')}
              </button>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button 
                className="font-medium text-left flex items-center"
                onClick={() => handleSort('depart_time')}
              >
                Time
                {getSortDirectionIndicator('depart_time')}
              </button>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button 
                className="font-medium text-left flex items-center"
                onClick={() => handleSort('cabin_class')}
              >
                Class
                {getSortDirectionIndicator('cabin_class')}
              </button>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button 
                className="font-medium text-left flex items-center"
                onClick={() => handleSort('points')}
              >
                Points
                {getSortDirectionIndicator('points')}
              </button>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button 
                className="font-medium text-left flex items-center"
                onClick={() => handleSort('fare_tax')}
              >
                Taxes
                {getSortDirectionIndicator('fare_tax')}
              </button>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Details
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedFlights.map((flight) => {
            const connections = parseConnections(flight.connections);
            const operating_airlines = parseAirlines(flight.operating_airlines);
            return (
              <React.Fragment key={flight.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(flight.depart_time, flight.origin)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTime(flight.depart_time, flight.origin)} - {formatTime(flight.arrive_time, flight.destination)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getClassColor(flight.cabin_class)}`}>
                      {flight.cabin_class || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                    {flight.points ? flight.points.toLocaleString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    ${flight.fare_tax ? flight.fare_tax.toFixed(2) : '0.00'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      flight.status === 'AVAILABLE' ? 'bg-green-100 text-green-800' : 
                      flight.status === 'WAITLIST' ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-red-100 text-red-800'
                    }`}>
                      {flight.status || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button
                      onClick={() => toggleRowExpansion(flight.id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {expandedRow === flight.id ? 'Hide' : 'Show'}
                    </button>
                  </td>
                </tr>
                
                {/* Expanded row */}
                {expandedRow === flight.id && (
                  <tr className="bg-gray-50">
                    <td colSpan="7" className="px-6 py-4">
                      <div className="text-sm text-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium mb-2">Flight Details</h4>
                            <p><span className="font-medium">Bundle:</span> {flight.bundle_code || 'N/A'}</p>
                            <p><span className="font-medium">Fare Code:</span> {flight.fare_code || 'N/A'}</p>
                            <p><span className="font-medium">Booking Class:</span> {flight.booking_class || 'N/A'}</p>
                            <p><span className="font-medium">Refundable:</span> {flight.refundable ? 'Yes' : 'No'}</p>
                            <p><span className="font-medium">Inventory:</span> {flight.inventory || 'Unknown'}</p>
                            <p><span className="font-medium">Origin:</span> {flight.origin}</p>
                            <p><span className="font-medium">Destination:</span> {flight.destination}</p>
                          </div>
                          
                          {/* Connection details if available */}
                          <div>
                            <h4 className="font-medium mb-2">Connection Details</h4>
                            {connections.length > 0 ? (
                              <div className="space-y-2">
                                {connections.map((connection, index) => (
                                  <div key={index} className="border-l-2 border-blue-500 pl-2">
                                    <p>Connection #{index + 1}</p>
                                    <p>
                                      <span className="font-medium">Airport:</span> {connection.airport || 'N/A'}
                                    </p>
                                    <p>
                                      <span className="font-medium">Duration:</span> {connection.duration || 'N/A'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p>Direct flight - no connections</p>
                            )}
                          </div>
                          {/* Operating Airline(s) details if available */}
                          <div>
                            <h4 className="font-medium mb-2">Airline Details</h4>
                            {operating_airlines.length > 0 ? (
                              <div className="space-y-2">
                                {operating_airlines.map((operating_airline, index) => (
                                  <div key={index} className="border-l-2 border-blue-500 pl-2">
                                    <p>Airline #{index + 1}</p>
                                    <p>
                                      <span className="font-medium">Airline:</span> {operating_airline.airline || 'N/A'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p>Unknown Operating Airline(s)</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FlightTable;